/**
 * §9.13 Billing tab, expert arm — live twin of the prototype
 * `ExpertBillingView`, rendering `BillingExpertArmView` off the ENG-5428 read
 * model. Layout and viewer rules are the prototype's; the as-built deltas:
 *
 *  - **No per-payment receipt links, permanently** — Stripe transfers have no
 *    receipt object in any design. The affordance is a link-out to the
 *    expert's own `/settings/payout` page, which owns the Express-dashboard
 *    login link.
 *  - Totals come off the wire, never client math: `totalEarned` is the api's
 *    sum of PAID payments; `maxEarnings` its snapshot-rate × NTE. A null
 *    `totalEarned` is the api's zero (`gql.Money` cannot express one), not a
 *    §6.3 denial — this arm's presence IS the entitlement — so it renders $0.
 *  - `connectPayoutsEnabled` is tri-state: null means the api's live Stripe
 *    read was unavailable — say "unavailable", never "not enabled".
 *  - "Hours covered" is a display-only join from `hoursSubmissionId` onto the
 *    Hub's already-loaded submissions, and reads CONFIRMED hours only; a
 *    payment whose submission isn't in view — or isn't confirmed yet —
 *    renders an em-dash rather than inventing hours.
 *  - An empty history can mean the payment platform isn't recording here yet,
 *    so the empty state never asserts "you have not been paid".
 */
import { Link } from 'react-router';

import { ExpertPaymentKind, ExpertPaymentStatus } from '@/__generated__/graphql';

import { formatDate } from '../../components/EngagementHub/format';
import Section from '../Section';
import { formatEngagementMoney, formatHours } from '../money';
import type { BillingExpertArmView, EngagementView, ExpertPaymentRow } from '../viewModel';
import ChargeHistory, { type ChargeRow } from './ChargeHistory';
import TonePill, { TONES, type Tone } from './TonePill';

interface ExpertBillingArmProps {
  arm: BillingExpertArmView;
  view: EngagementView;
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="text-on-surface-muted subtitle-2">{label}</div>
      <div className="mt-4 text-on-surface hd-5">{value}</div>
    </div>
  );
}

const PAYMENT_STATUS: Record<ExpertPaymentStatus, { label: string; tone: Tone }> = {
  [ExpertPaymentStatus.Paid]: { label: 'Paid', tone: TONES.moved },
  [ExpertPaymentStatus.Pending]: { label: 'Pending', tone: TONES.working },
  [ExpertPaymentStatus.Processing]: { label: 'Processing', tone: TONES.working },
  [ExpertPaymentStatus.Failed]: { label: 'Failed', tone: TONES.failed },
  [ExpertPaymentStatus.Canceled]: { label: 'Canceled', tone: TONES.quiet },
};

/** Non-work payments name their kind; a work fee is the unmarked case. */
const KIND_LABEL: Partial<Record<ExpertPaymentKind, string>> = {
  [ExpertPaymentKind.ReferralBonus]: 'Referral bonus',
  [ExpertPaymentKind.Goodwill]: 'Goodwill',
};

function statusCell(payment: ExpertPaymentRow): React.ReactNode {
  const { label, tone } = PAYMENT_STATUS[payment.status] ?? {
    label: 'Pending',
    tone: TONES.working,
  };
  return <TonePill tone={tone}>{label}</TonePill>;
}

function amountCell(payment: ExpertPaymentRow): React.ReactNode {
  const amount =
    payment.amountCents !== null
      ? formatEngagementMoney(payment.amountCents, payment.currency)
      : '—';
  const kind = KIND_LABEL[payment.kind];
  if (!kind) return amount;
  return (
    <span className="flex flex-col gap-2">
      <span>{amount}</span>
      <span className="text-on-surface-muted body-2">{kind}</span>
    </span>
  );
}

export default function ExpertBillingArm({ arm, view }: ExpertBillingArmProps): JSX.Element {
  const currency = arm.currency ?? 'USD';
  // Null is the api's zero, not a denial — see the module comment.
  const totalEarned = formatEngagementMoney(arm.totalEarnedCents ?? 0, currency);
  const maxEarnings =
    arm.maxEarningsCents !== null ? formatEngagementMoney(arm.maxEarningsCents, currency) : '—';

  // CONFIRMED hours only: payments generate off confirmed hours, so showing a
  // submitted-but-unconfirmed figure here would be exactly the "inventing
  // hours" the em-dash exists to avoid.
  const hoursFor = (payment: ExpertPaymentRow): string => {
    const submission = view.hoursSubmissions.find((s) => s.id === payment.hoursSubmissionId);
    const confirmed = submission?.hoursConfirmed ?? null;
    return confirmed !== null ? formatHours(confirmed) : '—';
  };

  const rows: ChargeRow[] = arm.payments.map((payment) => ({
    key: payment.id,
    cells: [
      ['Date', payment.paidAt ? formatDate(payment.paidAt) : '—'],
      ['Amount', amountCell(payment)],
      ['Hours covered', hoursFor(payment)],
      ['Status', statusCell(payment)],
    ],
  }));

  const connectStatus =
    arm.connectPayoutsEnabled === null
      ? 'Unavailable right now'
      : arm.connectPayoutsEnabled
        ? 'Payouts enabled'
        : 'Payouts not enabled';

  return (
    <div className="flex flex-col gap-24">
      <Section title="Earnings">
        <div className="grid grid-cols-1 gap-24 md:grid-cols-2">
          <Metric label="Total earned" value={totalEarned} />
          <Metric label="Max earnings" value={maxEarnings} />
        </div>
      </Section>

      <Section title="Payment history">
        <ChargeHistory rows={rows} />
        {rows.length === 0 ? (
          <p className="text-on-surface-muted body-2">
            No payments recorded here yet — they appear as your confirmed hours are paid out.
          </p>
        ) : null}
      </Section>

      <Section title="Stripe Connect">
        <div className="flex flex-wrap items-center gap-12">
          <span className="text-on-surface body-2">Account status: {connectStatus}</span>
          <Link to="/settings/payout" className="text-brand-primary body-2">
            Open payout settings
          </Link>
        </div>
      </Section>
    </div>
  );
}
